import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AssignUom } from 'bom/components/AssignUom';
import { IAssignUomForm, IUomTableRow } from 'bom/types';
import { AgGridTable } from 'core/components';
import { useAgGridClientTableContext } from 'core/states';
import { Color } from 'theme/enums';

interface IProps {
  onRowsChanged: () => void;
}

export const Table = ({ onRowsChanged }: IProps) => {
  const [closeSelection, setCloseSelection] = useState(false);
  const [isOpenAssignUom, setIsOpenAssignUom] = useState(false);

  const { t } = useTranslation();
  const { selectedRows, updateRows } = useAgGridClientTableContext();

  // Bulk "Assign Selected To" offers only the UOMs common to ALL selected rows, so one choice
  // clears every one of them — a row is resolved by a UOM in its own allowed set.
  const commonOptions = useMemo(() => {
    const rows = (selectedRows as IUomTableRow[]) ?? [];

    if (!rows.length) {
      return [];
    }

    return rows
      .map((row) => row.allowedUoms ?? [])
      .reduce((common, uoms) => common.filter((uom) => uoms.includes(uom)));
  }, [selectedRows]);

  const selectionActions = () => [
    {
      label: t('buttonLabelChangeSelectedTo'),
      color: Color.PRIMARY,
      onClick: () => setIsOpenAssignUom(true),
    },
    {
      label: t('buttonLabelOmitSelected'),
      color: Color.PRIMARY,
      onClick: onOmitSelected,
    },
  ];

  // Bulk actions stay local (mutate selected rows by reference, then refresh the grid). The save
  // happens on submit, alongside the per-row edits.
  const onAssignUomSubmit = ({ uom }: IAssignUomForm) => {
    (selectedRows as IUomTableRow[])?.forEach((row) => {
      row.assignedUom = uom;
    });

    updateRows({ update: selectedRows });
    onRowsChanged();

    handleOnClose();
  };

  const onOmitSelected = () => {
    (selectedRows as IUomTableRow[])?.forEach((row) => {
      row.omit = true;
    });

    updateRows({ update: selectedRows });
    onRowsChanged();

    return true;
  };

  const handleOnClose = () => {
    setIsOpenAssignUom(false);
    setCloseSelection(true);
  };

  return (
    <>
      <AgGridTable
        selectionActions={selectionActions}
        closeSelection={closeSelection}
        setCloseSelection={setCloseSelection}
      />

      <AssignUom
        isOpen={isOpenAssignUom}
        onClose={handleOnClose}
        onSubmit={onAssignUomSubmit}
        options={commonOptions}
      />
    </>
  );
};
